import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react'
import type { Room as ColyseusRoom } from 'colyseus.js'
import {
  createAndJoinRoom,
  findAndJoinRoom,
  getColyseusClient,
} from './lib/colyseus'
import { ensureAnonSession } from './lib/supabase'
import { useRoom } from './hooks/useRoom'
import Home from './components/Home'
import Lobby from './components/Lobby'
import Discussion from './components/Discussion'
import Ranking from './components/Ranking'
import Reveal from './components/Reveal'
import Results from './components/Results'
import Menu from './components/Menu'
import HostTransferBanner from './components/HostTransferBanner'
import SeatTakeoverPrompt from './components/SeatTakeoverPrompt'
import type { ClientMessage, MessageType } from '../shared/messages'

type SendFn = <T extends MessageType>(
  type: T,
  payload?: Extract<ClientMessage, { type: T }>['payload'],
) => void

function buildSend(colyseusRoom: ColyseusRoom | null): SendFn {
  return (type, payload) => {
    if (!colyseusRoom) return
    colyseusRoom.send(type as string, payload ?? {})
  }
}

const STORAGE_RECONNECT_KEY = 'prefs:reconnect'

interface ReconnectState {
  reconnectionToken: string
  code: string
  name: string
}

function loadReconnect(): ReconnectState | null {
  try {
    const raw = localStorage.getItem(STORAGE_RECONNECT_KEY)
    if (!raw) return null
    return JSON.parse(raw) as ReconnectState
  } catch {
    return null
  }
}

function saveReconnect(state: ReconnectState | null) {
  if (state) {
    localStorage.setItem(STORAGE_RECONNECT_KEY, JSON.stringify(state))
  } else {
    localStorage.removeItem(STORAGE_RECONNECT_KEY)
  }
}

interface RoomCtx {
  code: string
  name: string
}

export default function App() {
  const [colyseusRoom, setColyseusRoom] = useState<ColyseusRoom | null>(null)
  const [code, setCode] = useState<string | null>(null)
  const [playerName, setPlayerName] = useState<string>('')
  const [playerId, setPlayerId] = useState<string>('')
  const [booting, setBooting] = useState(true)
  const [reconnecting, setReconnecting] = useState(false)
  // overlayShown lags `reconnecting` by 600ms so quick reconnects (e.g.
  // brief screen-locks that finish under the threshold) leave the UI
  // untouched. Only longer recoveries surface a "Reconnecting…" message.
  const [overlayShown, setOverlayShown] = useState(false)
  const reconnectInFlight = useRef<Promise<ColyseusRoom> | null>(null)
  const wireRoomRef = useRef<((next: ColyseusRoom, ctx: RoomCtx) => void) | null>(null)
  const hiddenSinceRef = useRef<number | null>(null)
  const { room } = useRoom(colyseusRoom, code)

  // Tracks which existing player's id we've already prompted about, so the
  // takeover modal doesn't re-trigger on every state diff. Cleared on leave.
  const collisionPromptedFor = useRef<string | null>(null)
  const [collisionTargetId, setCollisionTargetId] = useState<string | null>(null)

  // Wire a Room's lifecycle: persist the reconnection token, and on a
  // transient disconnect (anything other than a 4xxx server kick) try
  // to reconnect using the saved token before falling back to Home.
  // Recursion is via wireRoomRef so the new Room from a successful
  // reconnect gets the same lifecycle hooks.
  const wireRoom = useCallback((next: ColyseusRoom, ctx: RoomCtx) => {
    if (next.reconnectionToken) {
      saveReconnect({
        reconnectionToken: next.reconnectionToken,
        code: ctx.code,
        name: ctx.name,
      })
    }

    next.onLeave(async (leaveCode) => {
      // 4xxx codes are server-initiated kicks (e.g. host kick).
      // Don't try to reconnect — clear everything and bail.
      if (leaveCode >= 4000) {
        saveReconnect(null)
        reconnectInFlight.current = null
        setReconnecting(false)
        setColyseusRoom((curr) => (curr === next ? null : curr))
        setCode((curr) => (curr === ctx.code ? null : curr))
        return
      }

      const saved = loadReconnect()
      if (!saved?.reconnectionToken) {
        setColyseusRoom((curr) => (curr === next ? null : curr))
        setCode(null)
        return
      }

      setReconnecting(true)
      try {
        if (!reconnectInFlight.current) {
          reconnectInFlight.current = getColyseusClient().reconnect(saved.reconnectionToken)
        }
        const reconnected = await reconnectInFlight.current
        reconnectInFlight.current = null
        wireRoomRef.current?.(reconnected, { code: saved.code, name: saved.name })
        setColyseusRoom(reconnected)
        setReconnecting(false)
      } catch (err) {
        console.warn('mid-session reconnect failed', err)
        reconnectInFlight.current = null
        saveReconnect(null)
        setReconnecting(false)
        setColyseusRoom((curr) => (curr === next ? null : curr))
        setCode((curr) => (curr === ctx.code ? null : curr))
      }
    })

    setColyseusRoom(next)
  }, [])

  wireRoomRef.current = wireRoom

  // Boot anon auth + try reconnect.
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const session = await ensureAnonSession()
        if (cancelled) return
        setPlayerId(session.user.id)

        const persisted = loadReconnect()
        if (persisted?.reconnectionToken) {
          try {
            const reconnected = await getColyseusClient().reconnect(persisted.reconnectionToken)
            if (cancelled) return
            setPlayerName(persisted.name)
            setCode(persisted.code)
            wireRoom(reconnected, { code: persisted.code, name: persisted.name })
          } catch (err) {
            console.warn('reconnect failed, clearing', err)
            saveReconnect(null)
          }
        }
      } catch (err) {
        console.error('boot failed', err)
      } finally {
        if (!cancelled) setBooting(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [wireRoom])

  // Defer the "Reconnecting…" overlay 600ms so a quick recovery is
  // invisible. If `reconnecting` flips back to false before the timer
  // fires, the overlay never appears at all.
  useEffect(() => {
    if (!reconnecting) {
      setOverlayShown(false)
      return
    }
    const t = setTimeout(() => setOverlayShown(true), 600)
    return () => clearTimeout(t)
  }, [reconnecting])

  // Proactive reconnect on resume. iOS/Android suspend backgrounded tabs
  // and their WebSockets — the close event arrives whenever the OS feels
  // like delivering it, sometimes seconds after unlock. Don't wait for
  // that: when the page becomes visible after being hidden long enough
  // that the WS is overwhelmingly likely dead, fire reconnect ourselves.
  // The `reconnectInFlight` ref dedupes against the onLeave-driven path
  // if the close event also fires concurrently. If we still hold a
  // colyseusRoom, the new room replaces it via setColyseusRoom; the old
  // room's eventual onLeave is a no-op because `curr !== oldRoom`.
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        hiddenSinceRef.current = Date.now()
        return
      }
      // visibilitystate === 'visible'
      const hiddenMs = hiddenSinceRef.current ? Date.now() - hiddenSinceRef.current : 0
      hiddenSinceRef.current = null

      if (reconnecting) return
      const saved = loadReconnect()
      if (!saved?.reconnectionToken) return

      // Two cases warrant a reconnect:
      //   passive: we have no room (WS already torn down).
      //   proactive: hidden for >3s; even if `colyseusRoom` looks set,
      //     the WS is likely dead and waiting on the close event would
      //     just delay recovery.
      const proactive = !!colyseusRoom && hiddenMs > 3000
      const passive = !colyseusRoom
      if (!proactive && !passive) return

      setReconnecting(true)
      ;(async () => {
        try {
          if (!reconnectInFlight.current) {
            reconnectInFlight.current = getColyseusClient().reconnect(saved.reconnectionToken)
          }
          const reconnected = await reconnectInFlight.current
          reconnectInFlight.current = null
          setPlayerName(saved.name)
          setCode(saved.code)
          wireRoom(reconnected, { code: saved.code, name: saved.name })
          setReconnecting(false)
        } catch (err) {
          console.warn('visibility-triggered reconnect failed', err)
          reconnectInFlight.current = null
          saveReconnect(null)
          setReconnecting(false)
        }
      })()
    }
    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => document.removeEventListener('visibilitychange', onVisibilityChange)
  }, [colyseusRoom, reconnecting, wireRoom])

  const handleCreateRoom = useCallback(async (name: string) => {
    const { room: r, code: newCode } = await createAndJoinRoom(name)
    setPlayerName(name)
    setCode(newCode)
    wireRoom(r, { code: newCode, name })
  }, [wireRoom])

  const handleJoinRoom = useCallback(async (joinCode: string, name: string) => {
    const { room: r, code: upper } = await findAndJoinRoom(joinCode, name)
    setPlayerName(name)
    setCode(upper)
    wireRoom(r, { code: upper, name })
  }, [wireRoom])

  const handleLeaveRoom = useCallback(() => {
    if (colyseusRoom) {
      colyseusRoom.leave(true)
    }
    setColyseusRoom(null)
    setCode(null)
    setPlayerName('')
    saveReconnect(null)
    collisionPromptedFor.current = null
    setCollisionTargetId(null)
  }, [colyseusRoom])

  // Detect a name collision on first join: another player in the room has the
  // same name (case-insensitive) but a different user id. Prompt the user
  // once to ask the host for a seat takeover.
  useEffect(() => {
    if (!room || !playerId || !playerName) return
    if (collisionTargetId) return
    const lower = playerName.trim().toLowerCase()
    if (!lower) return
    const match = Object.values(room.players).find(
      (p) => p.id !== playerId && p.name.trim().toLowerCase() === lower,
    )
    if (!match) return
    if (collisionPromptedFor.current === match.id) return
    collisionPromptedFor.current = match.id
    setCollisionTargetId(match.id)
  }, [room, playerId, playerName, collisionTargetId])

  const send: SendFn = buildSend(colyseusRoom)

  const reconnectingOverlay = overlayShown ? (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-warmblack/40 backdrop-blur-sm">
      <div className="bg-cream rounded-2xl px-6 py-4 shadow-xl font-display text-warmblack flex items-center gap-3">
        <span className="inline-block h-4 w-4 rounded-full border-2 border-warmblack/30 border-t-warmblack animate-spin" />
        Reconnecting…
      </div>
    </div>
  ) : null

  if (booting) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="font-body text-warmblack/40">Loading…</div>
      </div>
    )
  }

  if (!colyseusRoom || !room || !code) {
    return (
      <>
        <Home
          onCreateRoom={handleCreateRoom}
          onJoinRoom={handleJoinRoom}
          loading={false}
        />
        {reconnectingOverlay}
      </>
    )
  }

  let body: ReactElement
  switch (room.phase) {
    case 'lobby':
      body = (
        <Lobby
          room={room}
          playerId={playerId}
          playerName={playerName}
          onLeave={handleLeaveRoom}
          send={send}
        />
      )
      break
    case 'discussion':
      // The ranker skips the discussion screen and goes straight to ranking.
      // Non-rankers still see Discussion so they can chat about the items.
      body = playerId === room.currentRankerId
        ? <Ranking key="discussion-as-ranker" room={room} playerId={playerId} send={send} />
        : <Discussion room={room} playerId={playerId} send={send} />
      break
    case 'rankerRanking':
    case 'groupGuess':
    case 'individualGuess':
      body = <Ranking key={room.phase} room={room} playerId={playerId} send={send} />
      break
    case 'revealing':
    case 'lineReveal':
      body = <Reveal room={room} playerId={playerId} send={send} />
      break
    case 'results':
      body = <Results room={room} playerId={playerId} send={send} />
      break
    default:
      body = (
        <Home
          onCreateRoom={handleCreateRoom}
          onJoinRoom={handleJoinRoom}
          loading={false}
        />
      )
  }
  const collisionTarget = collisionTargetId ? room.players[collisionTargetId] : null

  return (
    <>
      <HostTransferBanner room={room} playerId={playerId} send={send} />
      {body}
      <Menu
        room={room}
        playerId={playerId}
        send={send}
        onChangeName={(newName) => {
          setPlayerName(newName)
          if (colyseusRoom?.reconnectionToken && code) {
            saveReconnect({
              reconnectionToken: colyseusRoom.reconnectionToken,
              code,
              name: newName,
            })
          }
        }}
      />
      {collisionTarget && (
        <SeatTakeoverPrompt
          existingName={collisionTarget.name}
          onClaim={() => {
            send('request_seat_takeover', { targetUserId: collisionTarget.id })
            setCollisionTargetId(null)
          }}
          onKeepNew={() => setCollisionTargetId(null)}
        />
      )}
      {reconnectingOverlay}
    </>
  )
}

export type { SendFn }
